<template>
  <div>
    <form @submit.prevent="submit" class="form-validation-js" novalidate>
      <div class="p-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-8">

        <!-- Name Field -->
        <FormInput
          v-model="form.name"
          :label="'Name *'"
          :error="showError('name')"
          :error-message="getErrorMsg('name')"
          :required="true"
          @blur="onBlur('name')"
          class="col-span-1 md:col-span-3"
        />

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 col-span-1 md:col-span-3">
          <!-- Email -->
          <FormInput
            v-model="form.email"
            :label="'Email *'"
            :type="'email'"
            :error="showError('email')"
            :error-message="getErrorMsg('email')"
            :required="true"
            :readonly="!!(client && user)"
            @blur="onBlur('email')"
          />
          <!-- Cell -->
          <FormInput
            v-model="form.cell"
            :label="'Cell *'"
            :error="showError('cell')"
            :error-message="getErrorMsg('cell')"
            :required="true"
            @blur="onBlur('cell')"
            @input="validateField('cell')"
          />
        </div>

        <FormInput
          v-model="form.address"
          :label="'Address *'"
          :error="showError('address')"
          :error-message="getErrorMsg('address')"
          :required="true"
          class="col-span-1 md:col-span-3"
          @blur="onBlur('address')"
        />
        <FormInput
          v-model="form.city"
          :label="'City *'"
          :error="showError('city')"
          :error-message="getErrorMsg('city')"
          :required="true"
          @blur="onBlur('city')"
        />

        <!-- State Select -->
        <FormSelect
          v-model="form.state"
          :label="'State *'"
          :options="states"
          option-label="name"
          option-value="state"
          :error="showError('state')"
          :error-message="getErrorMsg('state')"
          :required="true"
          @blur="onBlur('state')"
        />

        <FormInput
          v-model="form.postal_code"
          :label="'Postal Code *'"
          :maxlength="15"
          :error="showError('postal_code')"
          :error-message="getErrorMsg('postal_code')"
          :required="true"
          @blur="onBlur('postal_code')"
        />

        <!-- Country Select -->
        <FormSelect
          v-model="form.country_id"
          :label="'Country'"
          :options="countries"
          option-label="name"
          option-value="id"
          :error="showError('country_id')"
          :error-message="getErrorMsg('country_id')"
          :required="true"
          @blur="onBlur('country_id')"
          class="col-span-1 md:col-span-3"
        />

        <!-- Password/Confirm (Sign up only) -->
        <div v-if="!client || !user" class="grid grid-cols-1 sm:grid-cols-2 gap-6 col-span-1 md:col-span-3">
          <FormInput
            v-model="form.password"
            :label="'Password *'"
            type="password"
            :error="showError('password')"
            :error-message="getErrorMsg('password')"
            :required="true"
            @blur="onBlur('password')"
          />
          <FormInput
            v-model="form.password_confirmation"
            :label="'Confirm Password *'"
            type="password"
            :error="showError('password_confirmation')"
            :error-message="getErrorMsg('password_confirmation')"
            :required="true"
            @blur="onBlur('password_confirmation')"
          />
        </div>
      </div>
      <div class="flex justify-end mt-6">
       <button
        type="submit"
        class="btn btn-blue flex items-center justify-center"
        :disabled="loading"
      >
        <span v-if="loading" class="loader mr-2"></span>
        {{ loading ? 'Please wait...' : (client && user ? 'Update and Continue' : 'Continue') }}
      </button>
      </div>
    </form>
  </div>
</template>

<script>
import FormInput from '../inputs/TextInput.vue'   // Reusable Input
import FormSelect from '../forms/FormSelect.vue' // Reusable Select
import axios from 'axios';
import { formatCellNumber } from '../../utils/formatters';

export default {
  name: 'SignupForm',
  components: { FormInput, FormSelect },
  props: {
    formData: Object,
    client: Object,
    user: Object,
  },
  data() {
    return {
      loading: false,
      form: {
        name: '',
        email: '',
        address: '',
        city: '',
        state: '',
        postal_code: '',
        country_id: 16,
        password: '',
        password_confirmation: '',
        cell: '',
      },
      formErrors: {},
      touched: {},
      countries: [],
      states: [],
    }
  },
  mounted() {
    axios.get('/api/address-info').then((response) => {
      this.countries = response.data.countries;
      this.states = response.data.states;
    });
    if (this.formData) {
      this.form = { ...this.form, ...this.formData };
    } else if (this.client && this.user) {
      this.form = {
        ...this.form,
        name: this.user.name,
        email: this.user.email,
        address: this.client.address,
        city: this.client.city,
        state: this.client.state,
        postal_code: this.client.postal_code,
        country_id: this.client.country_id,
        cell: this.formatCellNumber(this.user.cell)
      };
    }
  },
  methods: {
    formatCellNumber,
    showError(field) {
      return !!(this.formErrors[field] && (this.touched[field] || this.formErrors[field]));
    },
    getErrorMsg(field) {
      return this.formErrors[field]?.[0] || '';
    },
    onBlur(field) {
      if (field === 'cell') {
        this.form.cell = this.formatCellNumber(this.form.cell);
      }
      this.$set(this.touched, field, true);
      this.validateField(field);
    },
    validateField(field) {
      // Required fields
      const requiredFields = [
        'name', 'email', 'address', 'city', 'state', 'postal_code', 'country_id', 'cell',
      ];
      if (!this.client || !this.user) {
        requiredFields.push('password', 'password_confirmation');
      }
      if (requiredFields.includes(field)) {
        if (!this.form[field] || (typeof this.form[field] === 'string' && this.form[field].trim() === '')) {
          this.$set(this.formErrors, field, ['This field is required.']);
          return false;
        } else if (this.formErrors[field] && this.formErrors[field][0] === 'This field is required.') {
          this.$delete(this.formErrors, field);
        }
      }
      // Email format
      if (field === 'email' && this.form.email) {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(this.form.email)) {
          this.$set(this.formErrors, 'email', ['Please enter a valid email address.']);
          return false;
        } else if (this.formErrors.email && this.formErrors.email[0] === 'Please enter a valid email address.') {
          this.$delete(this.formErrors, 'email');
        }
      }
      // US phone number format with country code for 'cell'
      if (field === 'cell' && this.form.cell) {
        const usPhonePattern = /^\+1[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;
        if (!usPhonePattern.test(this.form.cell.trim())) {
          this.$set(this.formErrors, 'cell', ['Enter cell with country code (e.g., +1-555-555-5555).']);
          return false;
        } else if (this.formErrors.cell && this.formErrors.cell[0] === 'Enter cell with country code (e.g., +1-555-555-5555).') {
          this.$delete(this.formErrors, 'cell');
        }
      }
      // Password min length
      if (field === 'password' && this.form.password && this.form.password.length > 0 && this.form.password.length < 6) {
        this.$set(this.formErrors, 'password', ['Password must be at least 6 characters.']);
        return false;
      } else if (field === 'password' && this.formErrors.password && this.formErrors.password[0] === 'Password must be at least 6 characters.' && this.form.password.length >= 6) {
        this.$delete(this.formErrors, 'password');
      }
      // Password confirmation match
      if ((field === 'password_confirmation' || field === 'password') && this.form.password && this.form.password_confirmation) {
        if (this.form.password !== this.form.password_confirmation) {
          this.$set(this.formErrors, 'password_confirmation', ['Passwords do not match.']);
          return false;
        } else if (this.formErrors.password_confirmation && this.formErrors.password_confirmation[0] === 'Passwords do not match.') {
          this.$delete(this.formErrors, 'password_confirmation');
        }
      }
      return true;
    },
    validateAllFields() {
      return Object.keys(this.form).every(this.validateField);
    },
    getFirstErrorMessage(errorsObj, fallback = 'Please check the form for errors.') {
      for (const key in errorsObj) {
        if (Array.isArray(errorsObj[key]) && errorsObj[key].length > 0) return errorsObj[key][0];
        if (typeof errorsObj[key] === 'object' && errorsObj[key] !== null) {
          const nested = this.getFirstErrorMessage(errorsObj[key]);
          if (nested) return nested;
        }
      }
      return fallback;
    },
    async submit() {
      this.formErrors = {};
      this.loading = true;
      if (!this.validateAllFields()) {
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Please check the form for errors.'
        });
        this.loading = false;
        return;
      }
      const isUpdate = !!(this.client && this.user);
      if (!isUpdate && this.form.password.length < 6) {
        this.formErrors.password = ["Password must be at least 6 characters."];
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Password must be at least 6 characters.'
        });
        this.loading = false;
        return;
      }
      const payload = {
        client: {
          name: this.form.name,
          address: this.form.address,
          city: this.form.city,
          state: this.form.state,
          postal_code: this.form.postal_code,
          country_id: this.form.country_id
        },
        user: {
          name: this.form.name,
          email: this.form.email,
          cell: this.form.cell,
          country_id: this.form.country_id
        }
      };
      if (isUpdate) {
        payload.client_id = this.client.id;
        payload.user_id = this.user.id;
      } else {
        payload.user.password = this.form.password;
        payload.user.password_confirmation = this.form.password_confirmation;
        if (this.form.password !== this.form.password_confirmation) {
          this.formErrors.password_confirmation = ["Passwords do not match."];
          this.$notify({
            type: 'error',
            title: 'Error',
            text: 'Passwords do not match.'
          });
          this.loading = false;
          return;
        }
      }
      try {
        const url = isUpdate ? '/signup/update' : '/signup';
        const resp = await axios.post(url, payload, { validateStatus: () => true });
        if (resp.data.errors) {
          this.formErrors = resp.data.errors;
          if (resp.data.errors['user.email']) {
            this.$notify({
              type: 'error',
              title: 'Email Exists',
              text: 'This email is already registered. Please log in or use another email.',
              duration: 2000
            });
            this.loading = false;
            return;
          }
          this.$notify({
            type: 'error',
            title: 'Error',
            text: this.getFirstErrorMessage(resp.data.errors)
          });
          this.loading = false;
          return;
        }
        if (resp.data.client && resp.data.user) {
          this.$notify({
            type: 'success',
            title: 'Success',
            text: resp.data.message || (isUpdate ? 'Update successful!' : 'Signup successful!')
          });
          this.$emit('signup-setup-complete', { client: resp.data.client, user: resp.data.user, formData: { ...this.form } });
        } else if (resp.data.message && resp.status !== 201) {
          this.formErrors = { general: [resp.data.message] };
          this.$notify({
            type: 'error',
            title: 'Error',
            text: resp.data.message
          });
          this.loading = false;
          return;
        }
      } catch (error) {
        this.formErrors = error.response?.data?.errors || {};
        this.$notify({
          type: 'error',
          title: 'Error',
          text: this.getFirstErrorMessage(this.formErrors, 'An unexpected error occurred.')
        });
        this.loading = false; 
      }
    },
  }
}
</script>
